import { useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { isApiError } from "@brad-tipper/app-frontend";
import { useForm } from "react-hook-form";
import { useLocalSearchParams, useRouter } from "expo-router";
import { z } from "zod";

import { FormField } from "~/components/composed/FormField";
import { TurnstileField } from "~/components/TurnstileField";
import {
  AppLink,
  AppText,
  Button,
  ButtonText,
  PasswordField,
  Row,
  Stack,
  TextField,
} from "~/components/ui";
import { AuthScreen } from "~/components/composed/AuthScreen";
import { useAuth } from "~/hooks/useAuth";
import { normalizeAuthRedirectPath } from "~/utils/auth-redirect";
import { loginSchema } from "~/utils/form-schemas";
import { getDevAuthDefaults } from "~/utils/runtime-config";
import { isTurnstileConfigured } from "~/utils/turnstile";
import {
  getCheckoutIntentFromPath,
  getCheckoutPath,
  getRegisterPath,
  readPendingCheckoutIntent,
} from "~/utils/signup-intent";
import { getPostAuthPath, hasPaidAccess } from "~/utils/onboarding";
import { sessionStore } from "~/api/client";
import { db } from "~/api/db";

type LoginValues = z.infer<typeof loginSchema>;

function getLoginErrorMessage(error: unknown) {
  if (!(error instanceof Error)) return "Sign-in failed.";
  if (isApiError(error) && error.code === "invalid_credentials") {
    return "Email or password is incorrect.";
  }
  return error.message;
}

export default function LoginScreen() {
  const router = useRouter();
  const params = useLocalSearchParams<{ callbackURL?: string }>();
  const { login, loginPending, refresh } = useAuth();
  const turnstileEnabled = isTurnstileConfigured();
  const devDefaults = getDevAuthDefaults();

  const loginForm = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: {
      email: devDefaults.email,
      password: devDefaults.password,
      captchaToken: "",
    },
  });

  const pendingCheckoutIntent = useMemo(() => readPendingCheckoutIntent(), []);
  const pendingCheckoutPath = pendingCheckoutIntent
    ? getCheckoutPath(pendingCheckoutIntent.billing, pendingCheckoutIntent.plan)
    : null;
  // Before auth we may only know a pending checkout; after login we resolve via profile.
  const destinationHint = normalizeAuthRedirectPath(
    params.callbackURL,
    pendingCheckoutPath ?? "/subscribe",
  );
  const registerIntent = getCheckoutIntentFromPath(destinationHint);
  const registerPath = registerIntent
    ? getRegisterPath(registerIntent)
    : "/register";
  const registerHref = `${registerPath}${registerPath.includes("?") ? "&" : "?"}callbackURL=${encodeURIComponent(destinationHint)}`;

  const resolveDestination = async () => {
    const nextUser = await refresh();
    const { data: workspaceRows } = await db
      .from("workspaces")
      .select("owner_id")
      .limit(20);
    const hasSharedWorkspace = Boolean(
      workspaceRows?.some((workspace) => workspace.owner_id !== nextUser?.id),
    );
    if (params.callbackURL) {
      return normalizeAuthRedirectPath(
        params.callbackURL,
        getPostAuthPath(nextUser, { hasSharedWorkspace }),
      );
    }
    if (pendingCheckoutPath && !hasPaidAccess(nextUser)) {
      return pendingCheckoutPath;
    }
    return getPostAuthPath(nextUser, { hasSharedWorkspace });
  };

  const submitLogin = loginForm.handleSubmit(async (values) => {
    if (turnstileEnabled && !values.captchaToken) {
      loginForm.setError("captchaToken", {
        message: "Complete the spam check.",
      });
      return;
    }
    try {
      await login({
        email: values.email,
        password: values.password,
        captchaToken: turnstileEnabled
          ? values.captchaToken || undefined
          : undefined,
      });
      // With two-factor on, no session exists yet; MfaGate asks for the code and
      // the (auth) layout then forwards the signed-in user.
      if (!sessionStore.get()) return;
      router.replace((await resolveDestination()) as never);
    } catch (error) {
      loginForm.setError("root", {
        message: getLoginErrorMessage(error),
      });
    }
  });

  return (
    <AuthScreen title="Sign in">
      <Stack>
        {loginForm.formState.errors.root?.message ? (
          <AppText tone="danger" size="xs">
            {loginForm.formState.errors.root.message}
          </AppText>
        ) : null}
        <FormField control={loginForm.control} name="email" label="Email">
          {(field) => (
            <TextField
              {...field}
              value={String(field.value ?? "")}
              keyboardType="email-address"
              autoCapitalize="none"
              autoCorrect={false}
              autoComplete="email"
              placeholder="you@example.com"
            />
          )}
        </FormField>
        <FormField control={loginForm.control} name="password" label="Password">
          {(field) => (
            <PasswordField
              {...field}
              value={String(field.value ?? "")}
              autoComplete="current-password"
              accessibilityLabel="Password"
            />
          )}
        </FormField>
        <Row between>
          <AppLink href="/forgot-password">Forgot password?</AppLink>
        </Row>
        {turnstileEnabled ? (
          <Stack gap="xs">
            <AppText size="xs" weight="medium">
              Spam protection
            </AppText>
            <TurnstileField
              onToken={(token) =>
                loginForm.setValue("captchaToken", token ?? "", {
                  shouldValidate: true,
                })
              }
            />
            {loginForm.formState.errors.captchaToken?.message ? (
              <AppText tone="danger" size="xs">
                {loginForm.formState.errors.captchaToken.message}
              </AppText>
            ) : null}
          </Stack>
        ) : null}
        <Button onPress={() => void submitLogin()} disabled={loginPending}>
          <ButtonText>{loginPending ? "Signing in..." : "Sign in"}</ButtonText>
        </Button>
        <AppLink href={registerHref as never}>
          Don&apos;t have an account? Create one
        </AppLink>
      </Stack>
    </AuthScreen>
  );
}
